import { Box, Card, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import {
	AutocompleteArrayInput,
	AutocompleteInput,
	ChipField,
	Datagrid,
	DateField,
	FunctionField,
	ReferenceArrayField,
	ReferenceArrayInput,
	ReferenceField,
	ReferenceInput,
	SelectArrayInput,
	SingleFieldList,
	TextField,
	TopToolbar,
	useListContext,
	usePermissions,
	useTranslate,
} from 'react-admin';
import { CustomizableDatagrid } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import FeatureIntro from '../../components/FeatureIntro';
import { CPFField } from '../../components/Fields';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import ManualLinkButton from '../../manual/components/ManualLinkButton';
import { OperationStatementType } from '../../models';
import { filterToQuery, formatAttendantLabel, formatNumber, formatPoints, linkPlaceType } from '../../utils';

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold'
};

const formatCurrency = value => formatNumber(Number(value) || 0, {
	style: 'currency',
	currency: 'BRL',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2
});

// Resgate não tem valor bruto/pago e abastecimento não tem valor de resgate: vazio em vez de "R$ 0,00".
const currencyOrDash = value => (value === null || value === undefined ? '-' : formatCurrency(value));

const formatPercent = value => formatNumber(Number(value) || 0, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });

const SummaryLine = ({ label, value, strong, hint }) => (
	<TableRow>
		<TableCell sx={{ fontWeight: strong ? 'bold' : 'normal', borderBottom: strong ? '2px solid' : undefined }}>
			{label}
			{hint && <Typography variant="caption" component="div" color="text.secondary">{hint}</Typography>}
		</TableCell>
		<TableCell align="right" sx={{ fontWeight: strong ? 'bold' : 'normal', whiteSpace: 'nowrap', borderBottom: strong ? '2px solid' : undefined }}>
			{value}
		</TableCell>
	</TableRow>
);

// Resumo no formato de DRE: das vendas brutas ao resultado depois do que o programa devolveu ao cliente.
const StatementSummary = ({ summary }) => {
	if (!summary) { return null; }

	const programCost = Number(summary.discount_value) + Number(summary.cashback_generated) + Number(summary.redemption_value);
	const costShare = Number(summary.gross_value) > 0 ? programCost / Number(summary.gross_value) : 0;
	const productRedemptionValue = Number(summary.redemption_value) - Number(summary.cashback_redeemed);

	return (
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 6 }}>
			<Card variant="outlined" sx={{ flex: '1 1 420px', maxWidth: 640 }}>
				<Table size="small">
					<TableBody>
						<SummaryLine label="Vendas brutas (abastecimentos e PDV)" value={formatCurrency(summary.gross_value)} />
						<SummaryLine label="(−) Descontos concedidos" value={formatCurrency(summary.discount_value)} />
						<SummaryLine label="(=) Valor pago pelos clientes" value={formatCurrency(summary.paid_value)} strong />
						<SummaryLine label="(−) Cashback gerado nos abastecimentos" value={formatCurrency(summary.cashback_generated)} />
						<SummaryLine
							label="(−) Resgates em dinheiro"
							value={formatCurrency(summary.redemption_value)}
							hint={`Cashback ${formatCurrency(summary.cashback_redeemed)} · Produtos ${formatCurrency(productRedemptionValue)}`}
						/>
						<SummaryLine label="(=) Resultado depois do programa" value={formatCurrency(summary.net_result)} strong />
						<SummaryLine
							label="Custo do programa"
							value={`${formatCurrency(programCost)} · ${formatPercent(costShare)}`}
							hint="Descontos + cashback gerado + resgates em dinheiro, sobre as vendas brutas"
						/>
					</TableBody>
				</Table>
			</Card>
			<Card variant="outlined" sx={{ flex: '1 1 280px', maxWidth: 380 }}>
				<Table size="small">
					<TableBody>
						<SummaryLine label="Abastecimentos" value={formatPoints(summary.fillins)} />
						<SummaryLine label="Vendas no PDV" value={formatPoints(summary.sales)} />
						<SummaryLine label="Resgates de produto" value={formatPoints(summary.product_redemptions)} />
						<SummaryLine label="Resgates de cashback" value={formatPoints(summary.cashback_redemptions)} />
						<SummaryLine label="Pontos gerados" value={formatPoints(Number(summary.points_earned))} />
						<SummaryLine label="Pontos usados" value={formatPoints(Number(summary.points_used))} />
					</TableBody>
				</Table>
				{summary.redemptions_without_value > 0 &&
					<Typography variant="caption" component="p" color="text.secondary" sx={{ p: 1.5 }}>
						{`${summary.redemptions_without_value} resgates de produto sem preço de parceiro cadastrado não entram no custo.`}
					</Typography>
				}
			</Card>
		</Box>
	);
};

// Abastecimento mostra o combustível e os litros; resgate, o produto. Venda do PDV não traz item.
const renderItem = record => {
	if (record.fuel_id) {
		return (
			<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
				<ReferenceField source="fuel_id" reference="fuels" link={false}>
					<TextField source="name" />
				</ReferenceField>
				{record.amount ? ` · ${formatNumber(Number(record.amount), { maximumFractionDigits: 2 })} L` : ''}
			</Box>
		);
	}
	if (record.product_id) {
		return (
			<ReferenceField source="product_id" reference="chains/chainId/products" link={false}>
				<TextField source="name" />
			</ReferenceField>
		);
	}
	return '-';
};

const OperationsList = () => {
	const props = useListContext();
	const { permissions } = usePermissions();

	const reportInfo = props.data && props.data[0];
	const rows = (reportInfo && reportInfo.data) || [];
	const summary = reportInfo && reportInfo.summaryData;

	return (
		<>
			<Typography sx={titleSx}>Resumo do período</Typography>
			<StatementSummary summary={summary} />

			<Typography sx={titleSx}>Operações</Typography>
			<CustomizableDatagrid bulkActionButtons={false} resource={props.resource} data={rows} hover={false}>
				<DateField source="occurred_at" label="Data/Hora" showTime locales="pt-BR" />
				<FunctionField source="type" label="Tipo de operação" sortable={false} render={record => OperationStatementType.translateType(record.type)} />
				<ReferenceField
					source="place_id"
					label="Posto"
					reference="places"
					emptyText="Não identificado"
					link={permissions?.includes(PERMISSIONS.LIST_STATION) && linkPlaceType}
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<TextField source="customer_name" label="Cliente" emptyText="Não cadastrado" sortable={false} />
				<CPFField source="customer_cpf" label="CPF" sortable={false} />
				<FunctionField source="item" label="Combustível / produto" sortable={false} render={renderItem} />
				<FunctionField source="gross_value" label="Valor bruto" textAlign="right" render={record => currencyOrDash(record.gross_value)} />
				<FunctionField source="discount_value" label="Desconto" textAlign="right" render={record => currencyOrDash(record.discount_value)} />
				<FunctionField source="paid_value" label="Valor pago" textAlign="right" render={record => currencyOrDash(record.paid_value)} />
				<FunctionField source="points_earned" label="Pontos gerados" textAlign="right" render={record => formatPoints(Number(record.points_earned))} />
				<FunctionField source="points_used" label="Pontos usados" textAlign="right" render={record => formatPoints(Number(record.points_used))} />
				<FunctionField source="redemption_value" label="Valor do resgate" textAlign="right" render={record => currencyOrDash(record.redemption_value)} />
				<FunctionField source="cashback_generated" label="Cashback gerado" textAlign="right" sortable={false} render={record => formatCurrency(record.cashback_generated)} />
				<FunctionField source="net_value" label="Valor líquido" textAlign="right" render={record => formatCurrency(record.net_value)} />
				<FunctionField source="coupon_status" label="Situação do cupom" sortable={false} render={record => OperationStatementType.translateCouponStatus(record.coupon_status) || '-'} />
				<Permission label="Frentista" permission={PERMISSIONS.LIST_ATTENDANT}>
					<ReferenceField source="attendant_id" label="Frentista" reference="attendants" emptyText="-" link="show" sortable={false}>
						<FunctionField render={record => formatAttendantLabel(record)} />
					</ReferenceField>
				</Permission>
				<FunctionField source="origin" label="Origem" sortable={false} render={record => OperationStatementType.translateOrigin(record.type, record.origin)} />
			</CustomizableDatagrid>
		</>
	);
};

// Posto, frentista, combustível e produto saem da API como id; o nome vem por lazy load aqui, como no
// ReferenceField da tela. Frentista só entra pra quem vê a coluna (LIST_ATTENDANT): a planilha não
// pode mostrar o que a tela esconde (ver invariant_exporter_relation_scope_gating).
const makeOperationsExporter = (canSeeAttendants) => async (data, fetchRelatedRecords) => {
	const rows = data[0].data;

	const relations = [
		{ field: 'place_id', resource: 'places' },
		{ field: 'fuel_id', resource: 'fuels' },
		{ field: 'product_id', resource: 'chains/chainId/products' },
	];
	if (canSeeAttendants) { relations.push({ field: 'attendant_id', resource: 'attendants' }); }

	const fetched = await fetchRelatedRecords(relations, rows);

	const nameOf = (resource, id) => {
		const found = (fetched.find(({ resource: res }) => res === resource)?.results || []).find(item => item.id === id);
		return found ? found.name : '';
	};

	// Valores do banco vêm com até 4 casas (real × litros): a planilha sai com centavos.
	const money = value => (value === null || value === undefined ? '' : Math.round(Number(value) * 100) / 100);

	return rows.map(row => ({
		'Data/Hora': moment(row.occurred_at).format('DD/MM/YYYY HH:mm'),
		'Tipo de operação': OperationStatementType.translateType(row.type),
		'Código da operação': row.id,
		'Posto': nameOf('places', row.place_id),
		'Cliente': row.customer_name || 'Não cadastrado',
		'CPF': row.customer_cpf || '',
		'Combustível / produto': row.fuel_id ? nameOf('fuels', row.fuel_id) : nameOf('chains/chainId/products', row.product_id),
		'Litros': row.amount ?? '',
		'Valor bruto': money(row.gross_value),
		'Desconto': money(row.discount_value),
		'Valor pago': money(row.paid_value),
		'Pontos gerados': Number(row.points_earned),
		'Pontos usados': Number(row.points_used),
		'Valor do resgate': money(row.redemption_value),
		'Cashback gerado': money(row.cashback_generated),
		'Valor líquido': money(row.net_value),
		'Situação do cupom': OperationStatementType.translateCouponStatus(row.coupon_status),
		...(canSeeAttendants ? { 'Frentista': nameOf('attendants', row.attendant_id) } : {}),
		'Origem': OperationStatementType.translateOrigin(row.type, row.origin),
	}));
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
			<ReferenceArrayField filter={{ chainId: getChainId() }} label="Postos" emptyText="Todos" source="stationIds" textAlign="center" reference="places" link={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<FunctionField
				label="Tipos"
				textAlign="center"
				render={record => record.types && record.types.length ? record.types.map(OperationStatementType.translateType).join(', ') : 'Todos'}
			/>
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

const ReportOperationsStatement = (props) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();
	const isAdmin = permissions && permissions.includes(PERMISSIONS.ADMIN);
	const placeId = getPlaceId();

	const listFilters = [
		<BetweenDatesInput alwaysOn />,
		isAdmin &&
		<ReferenceInput source="chainId" reference="chains" perPage={null} sort={{ field: "name", order: "ASC" }} alwaysOn>
			<AutocompleteInput filterToQuery={filterToQuery} style={{ minWidth: 180 }} optionText="name" label="Rede" />
		</ReferenceInput>,
		!placeId &&
		<ReferenceArrayInput filter={{ chainId: getChainId() }} source="stationIds" reference="places" perPage={null} sort={{ field: "name", order: "ASC" }} alwaysOn>
			<AutocompleteArrayInput
				filterToQuery={filterToQuery}
				label="Posto"
				sx={{ minWidth: 180, maxWidth: 420, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
				optionText="name"
				noOptionsText={translate('ra.input.autocomplete.no_options_text')}
			/>
		</ReferenceArrayInput>,
		<SelectArrayInput
			source="types"
			label="Tipo de operação"
			choices={OperationStatementType.operationStatementTypeChoices}
			sx={{ minWidth: 200, maxWidth: 420 }}
			alwaysOn
		/>,
		permissions?.includes(PERMISSIONS.LIST_ATTENDANT) &&
		<ReferenceArrayInput source="attendantIds" reference="attendants" perPage={null} sort={{ field: "name", order: "ASC" }}>
			<AutocompleteArrayInput
				filterToQuery={filterToQuery}
				label="Frentista"
				sx={{ minWidth: 180, maxWidth: 420 }}
				optionText={formatAttendantLabel}
				noOptionsText={translate('ra.input.autocomplete.no_options_text')}
			/>
		</ReferenceArrayInput>,
	].filter(Boolean);

	return (
		<Permission permission={PERMISSIONS.OPERATIONS_STATEMENT_REPORT}>
			<>
				<TopToolbar sx={{ justifyContent: 'flex-start', minHeight: 'auto', pb: 0 }}>
					<FeatureIntro
						id="manual-extrato-de-operacoes"
						title="Extrato de operações"
						description="Veja como cada valor do extrato é calculado: vendas, descontos, cashback, resgates e o resultado do período."
					>
						<ManualLinkButton slug="extrato-de-operacoes" label="Como funciona" />
					</FeatureIntro>
				</TopToolbar>
				<ReportList periodRequired
					{...props}
					basePath="transactions"
					title="Extrato de operações"
					resource="chains/chainId/reports/operations-statement"
					filters={listFilters}
					sort={{ field: 'occurred_at', order: 'DESC' }}
					filterDefaultValues={{
						stationIds: placeId ? [placeId] : null,
						chainId: getChainId(),
						from: moment().startOf('month').toISOString(),
						to: moment().endOf('day').toISOString()
					}}
					bulkActionButtons={false}
					customExporter={makeOperationsExporter(!!permissions?.includes(PERMISSIONS.LIST_ATTENDANT))}
					titleOnPrint="Extrato de operações"
					filterTablePrint={<FilterTablePrint />}
					showFilterButton
					landscape
				>
					<OperationsList />
				</ReportList>
			</>
		</Permission>
	);
};

export default ReportOperationsStatement;
